import React from 'react'
import { FormattedDate, FormattedNumber } from 'react-intl'
import { isFinite, isEmpty } from 'lodash-es'

import ordinalize from 'lib/ordinalize'
import { IContestDetailsResponse } from 'protos'
import { ContestGoalTypeExplanationToken } from 'lib/enumTranslations'

import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'

import T from 'components/FormattedMessage'
import Currency from 'components/Currency'
import EventPrize from 'components/EventPrize'

export interface Props {
  teams?: IContestDetailsResponse['team']
  contest?: IContestDetailsResponse['contest']
  matches?: IContestDetailsResponse['match']
  prizePool?: IContestDetailsResponse['prizePool']
  competitions?: IContestDetailsResponse['competition']
}

const EventRules: React.SFC<Props> = ({
  teams,
  contest,
  matches,
  prizePool,
  competitions,
}) => {
  return (
    <div style={{ padding: 16 }}>
      <Grid direction="column" spacing={1} container>
        {contest && contest.title && (
          <Grid item>
            <Typography variant="body1"><T id="contests_rules_name" /></Typography>
            <Typography>{contest.title}</Typography>
          </Grid>
        )}

        {contest && !!contest.startTime && !!contest.endTime && (
          <Grid item>
            <Typography variant="body1"><T id="contests_rules_date" /></Typography>
            <Typography>
              <FormattedDate
                value={+contest.startTime * 1000}
                year="numeric"
                month="2-digit"
                day="2-digit"
                hour="numeric"
                minute="2-digit"
              />
              {' - '}
              <FormattedDate
                value={+contest.endTime * 1000}
                year="numeric"
                month="2-digit"
                day="2-digit"
                hour="numeric"
                minute="2-digit"
              />
            </Typography>
          </Grid>
        )}

        {contest && !!contest.goal && (
          <Grid item>
            <Typography variant="body1"><T id="contests_rules_goal" /></Typography>
            <Typography>
              {isFinite(contest.goal.type) && (
                <>
                  <T
                    id={ContestGoalTypeExplanationToken(contest.goal.type!)}
                    values={{ count: contest.goal.x }}
                  />
                  <br />
                </>
              )}

              <T id={contest.autoMmOnly ? 'contests_rules_amm_yes' : 'contests_rules_amm_no'} />

              {!!contest.oneTicketPerMatch && (
                <>
                  <br />
                  <T id="contests_rules_onegamepermatch" />
                </>
              )}
            </Typography>
          </Grid>
        )}

        {contest && (
          <>
            <Grid item>
              <Typography variant="body1"><T id="contests_rules_entryfee" /></Typography>
              <Typography>
                {contest.entryFee
                  ? <Currency {...contest.entryFee} />
                  : <T id="contests_rules_entryfee_free" />
                }
              </Typography>
            </Grid>

            <Grid item>
              <Typography variant="body1"><T id="contests_rules_participants" /></Typography>
              <Typography>
                {contest.maxParticipants
                  ? <FormattedNumber value={contest.maxParticipants} />
                  : <T id="contests_rules_participants_unlimited" />
                }
              </Typography>
            </Grid>
          </>
        )}

        {!isEmpty(prizePool) && (
          <Grid item>
            <Typography variant="body1"><T id="contests_rules_prizes" /></Typography>
            {prizePool!.map((poolItem) => (
              <Typography key={`${poolItem.fromRank}-${poolItem.toRank}`}>
                {poolItem.fromRank && ordinalize(poolItem.fromRank)}
                {poolItem.toRank && poolItem.fromRank !== poolItem.toRank && ` - ${ordinalize(poolItem.toRank)}`}
                {`: `}
                {poolItem.prize && <EventPrize prizes={poolItem.prize} />}
              </Typography>
            ))}
          </Grid>
        )}

        {!isEmpty(competitions) && (
          <Grid item>
            <Typography variant="body1"><T id="contests_rules_competitions" /></Typography>
            {competitions!.map((competition) => (
              <Typography key={`competition-${competition.name}`}>
                {competition.name}
              </Typography>
            ))}
          </Grid>
        )}

        {!isEmpty(teams) && (
          <Grid item>
            <Typography variant="body1"><T id="contests_rules_teams" /></Typography>
            {teams!.map((team) => (
              <Typography key={`team-${team.name}`}>
                {team.name}
              </Typography>
            ))}
          </Grid>
        )}

        {(!isEmpty(matches) || (isEmpty(teams) && isEmpty(competitions))) && (
          <Grid item>
            <Typography variant="body1"><T id="contests_rules_matches" /></Typography>
            {!isEmpty(matches) && (
              matches!.map((match) => (
                <Typography key={`match-${match.homeTeam!.name}-${match.awayTeam!.name}`}>
                  {`${match.homeTeam!.name} vs ${match.awayTeam!.name}`}
                </Typography>
              ))
            )}
            {isEmpty(matches) && (
              <Typography>
                <T id="contests_rules_matches_all" />
              </Typography>
            )}
          </Grid>
        )}
      </Grid>
    </div>
  )
}

export default EventRules
